import {
  type CSSProperties,
  Fragment,
  type KeyboardEvent,
  type MouseEvent,
  type MutableRefObject,
  type ReactElement,
  type Ref,
  forwardRef,
  useCallback,
  useLayoutEffect,
  useMemo,
} from 'react';
import { type Row, type VisibilityState, flexRender } from '@tanstack/react-table';
import { ImageMinus, Trash2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DeleteNodeMenuItem,
  DownloadMenuItem,
  DownloadZipMenuItem,
  DuplicateAssetMenuItem,
  OpenParentFolderMenuItem,
  RestoreTrashMenuItem,
  ShareAssetMenuItem,
  ShareDownloadMenuItem,
} from '@/components/menu-items/node-menu-items';
import { type DoubleClickTriggerEvent, type SelectionEventProps } from '@/components/layouts/gallery-layout';
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from '@/components/ui/context-menu';
import { MenuProvider, MenuSeparator, contextPrimitives } from '@/components/menu-renderer';
import { useTableSelection } from '@/hooks/data/datatable/useTableSelection';
import { columnWidthStyle } from '@/components/data-table/table-layout';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { useIsSearching } from '@/hooks/data/search/useIsSearching';
import { useSelection } from '@/hooks/data/selection/useSelection';
import { useShareDownload } from '@/context/ShareDownloadContext';
import { TableCell, TableRow } from '@/components/ui/table';
import { type Asset, isAssetLoading } from '@/types/asset';
import { useCapabilities } from '@/hooks/useCapabilities';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { useTreeContext } from '@/context/TreeContext';
import { DAMNodeType, NodeType } from '@/types/tree';
import { cn, isSelectableAsset } from '@/lib/utils';
import { DraggableItems } from '@/types/dragDrop';
import { TaskState } from '@/types/uploads';
import { useTranslations } from 'next-intl';

export const groupedColumns = {
  selection: {
    keys: ['select'],
    className: 'absolute left-6 top-6 z-50',
  },
  thumbnail: { keys: ['thumbnail'], className: 'relative' },
  ratingAndCount: {
    keys: ['rating', 'commentCount'],
    className: 'flex w-full items-center justify-between pt-2 pb-1',
  },
  nameAndFormat: { keys: ['name', 'format', 'type'], className: 'mb-px mt-1 flex w-full items-center gap-4' },
  fileDetails: { keys: ['fileName', 'size'], className: 'flex w-full gap-4' },
  other: { keys: ['description', 'slug', 'nodeId', 'progress'], className: '' },
  count: { keys: ['count'], className: 'flex w-full justify-between gap-4' },
};

export type DraggableItemProps<TData extends Asset> = {
  isRow?: boolean;
  row: Row<TData>;
  assets: Array<TData>;
  parentNodeId?: string;
  columnVisibility?: VisibilityState;
  handleClick?: (row: Row<TData>, event: SelectionEventProps) => void;
  // Always Asset, not TData: the Enter-key path (useEnterToOpenAsset, wired in
  // data-table-grid-list-view.tsx) resolves its target from GallerySelectionContext's Asset-typed
  // selection, not from a row of this table's own data.
  handleDoubleClick?: (asset: Asset, event: DoubleClickTriggerEvent) => void;
  handleRightClick?: (asset: TData) => void;
  isSearchingInTrash?: boolean;
  enableDnd: boolean;
  isUpload?: boolean;
  rowCount?: number;
  canReorderAssets?: boolean;
};

const DraggableItemInner = forwardRef(
  <TData extends Asset>(
    {
      isRow,
      row,
      assets,
      parentNodeId,
      handleClick,
      handleDoubleClick,
      handleRightClick,
      isSearchingInTrash = false,
      enableDnd,
      isUpload,
      rowCount,
      canReorderAssets,
    }: DraggableItemProps<TData>,
    forwardedRef: Ref<HTMLTableRowElement | HTMLDivElement>,
  ) => {
    const isSelected = row.getIsSelected();

    const { selectedNode, selectedAssets, isAllSelected, operationTarget: target } = useSelection();
    const { includeSubFolderAssets } = useTreeContext();
    const { isTrashPage, isDuplicatesPage, isSharesPage } = useCurrentPage();
    const { canDragAssets, canViewContextMenu, canDownloadAssets } = useCapabilities();
    const shareDownload = useShareDownload();

    const tSelection = useTranslations('selection');
    const search = useIsSearching();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: row.original.id,
      data: {
        id: row.original.id,
        type: DraggableItems.ASSET,
        asset: row.original,
        parentId: parentNodeId,
        accepts: DraggableItems.ASSET,
      },
      disabled: Boolean(!enableDnd),
    });

    const someTrashedAssetsSelected = useMemo(
      () => selectedAssets.some((selection) => selection.trashed),
      [selectedAssets],
    );

    const onlyTrashedAssetsSelected = useMemo(
      () => selectedAssets.every((selection) => selection.trashed),
      [selectedAssets],
    );

    /*Merge `forwardedRef` and dnd‑kit ref */
    const combinedRef = useCallback(
      (node: HTMLDivElement | HTMLTableRowElement | null) => {
        setNodeRef(node);
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          (forwardedRef as MutableRefObject<HTMLDivElement | HTMLTableRowElement | null>).current = node;
        }
      },
      [forwardedRef, setNodeRef],
    );

    // Only neutralizes Enter here — it does not open anything itself. dnd-kit's own keyboard
    // sensor (bound via `listeners` below) treats Enter as pick-up/drop for a keyboard-initiated
    // drag, which otherwise fires onDragStart and freezes the page (see setDraggingBodyClass in
    // DragAndDropContext) until a second Enter drops it, so it must never see this keypress —
    // achieved simply by never forwarding it to `listeners.onKeyDown` below, not by calling
    // `preventDefault()` (a plain div/tr has no default Enter behavior to prevent anyway).
    // Deliberately NOT calling preventDefault: useEnterToOpenAsset's global `window` listener
    // (wired in data-table-grid-list-view.tsx) is what actually opens the asset, and it bails out
    // on `event.defaultPrevented` to defer to any handler that already claimed the keypress — a
    // preventDefault() here, during the bubble phase before the event reaches `window`, would
    // make every Enter press on a focused row look already-handled and silently do nothing.
    // Opening globally rather than here is what keeps Enter working when the last-selected asset
    // isn't even rendered (its row was on a page you've since navigated away from).
    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter') {
          return;
        }
        listeners?.onKeyDown?.(event);
      },
      [listeners],
    );

    const style: CSSProperties = {
      ...(canReorderAssets && {
        transform: CSS.Transform.toString(transform),
        transition,
      }),
      opacity: isDragging ? 0 : 1,
      zIndex: isDragging ? 1 : 0,
      position: 'relative',
    };

    const { getActiveUploadSession } = useUploadStore();
    const {
      session: { uploadState, selectedAssets: uploadSelectedAssetIds },
    } = getActiveUploadSession();

    const trashPage = isTrashPage || isSearchingInTrash || row.original.trashed;

    // The visible mark lives on the thumbnail (see AssetStateOverlay in asset-thumbnail),
    // which is the one place that reads the same in the grid and the list. What is needed
    // here is to stop the row responding at all, on the same test the selection pool uses
    // (`isSelectableAsset`): a row a live broadcast has taken out of this list cannot be
    // selected, so it must not offer hover, a context menu, drag or a keyboard focus stop
    // either. Half of each was the bug - the row ignored a left click but still selected on
    // right click, and still offered actions the server now rejects.
    const isInert = !isSelectableAsset(row.original);

    const { handleSelection } = useTableSelection({ items: assets });

    useLayoutEffect(() => {
      if (isDragging && !isSelected) {
        handleSelection(row);
      }
    }, [isDragging, isSelected, row, handleSelection]);

    const TableComponents = isRow ? (
      <TableRow
        ref={combinedRef}
        {...(isSelected && { 'data-state': 'selected' })}
        data-row-id={row.original.id}
        {...(handleDoubleClick &&
          !isInert && {
            onDoubleClick: (event) => handleDoubleClick(row.original, event),
          })}
        {...(!isInert && { onClick: (event: MouseEvent<HTMLElement>) => handleClick?.(row, event) })}
        style={style}
        // Outside the canDragAssets spread: selecting what you right-clicked has nothing to do with
        // being allowed to drag it. Nested inside, a viewer who cannot drag - the public share
        // gallery, chiefly - got a context menu acting on whatever happened to be selected before.
        // Still off for a tombstone, like the click handlers above: there is nothing left to act on.
        {...(!isInert && { onContextMenu: () => handleRightClick?.(assets[row.index]) })}
        {...(canDragAssets &&
          !isInert && {
            ...attributes,
            ...listeners,
          })}
        {...(!isInert && { tabIndex: 0, onKeyDown: handleKeyDown })}
        className={cn('cursor-pointer outline-hidden', isInert && 'pointer-events-none', {
          'border-b-transparent': isDuplicatesPage,
        })}
        data-cy={`table-row-${row.index}`}
        id={row.original.id}
      >
        {row
          .getVisibleCells()
          .map(
            (cell: {
              id: string;
              column: { id: string; columnDef: any; getSize: () => number };
              getContext: () => any;
            }) => {
              return (
                <TableCell
                  key={cell.id}
                  data-cy={`list-cell-${row.index}-${cell.column?.id}`}
                  // Under table-fixed the header row dictates column widths; match its
                  // flexWidth logic here so the body never contradicts it. (DM-2393)
                  style={columnWidthStyle(cell.column)}
                  className={cn('select-none', cell.column.columnDef.meta?.cellClassName)}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              );
            },
          )}
      </TableRow>
    ) : (
      <div
        ref={combinedRef}
        {...(isSelected && { 'data-state': 'selected' })}
        {...(handleDoubleClick &&
          !isInert && {
            onDoubleClick: (event) => handleDoubleClick(row.original, event),
          })}
        {...(!isInert && { onClick: (event: MouseEvent<HTMLElement>) => handleClick?.(row, event) })}
        style={style}
        // See the row branch above: right-click selection is independent of drag capability.
        {...(!isInert && { onContextMenu: () => handleRightClick?.(assets[row.index]) })}
        {...(canDragAssets &&
          !isInert && {
            ...attributes,
            ...listeners,
          })}
        {...(!isInert && { tabIndex: 0, onKeyDown: handleKeyDown })}
        className={cn('relative h-full cursor-pointer p-1.5 outline-hidden', isInert && 'pointer-events-none')}
        data-cy={`tile-item-${row.index}`}
        id={row.original.id}
      >
        <div
          className={cn(
            'group/asset-container h-full select-none p-3 transition-colors duration-150 hover:bg-[#c8c8c8] dark:hover:bg-[#3b3b3b]',
            isSelected && 'bg-ui-light hover:bg-[#c8c8c8] dark:hover:bg-[#3b3b3b]',
          )}
        >
          {Object.entries(groupedColumns).map(([group, { keys, className }]) => (
            <div key={group} className={className} data-cy={`tile-item-${row.index}-${group}`}>
              {keys.map((colKey) => {
                const cell = row.getVisibleCells().find((c) => (c.column.columnDef as any).accessorKey === colKey);

                return cell ? (
                  <Fragment key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Fragment>
                ) : null;
              })}
            </div>
          ))}
        </div>
      </div>
    );

    // Excluding right click context menu for public route
    if (!canViewContextMenu) {
      // On a public share that allows downloads, guest/authenticated viewers still
      // get a download-only context menu acting on the current selection, via
      // ShareDownloadContext. Anonymous viewers (canDownloadAssets false) and
      // shares with nothing selected get no menu at all rather than an empty one.
      if (shareDownload && canDownloadAssets && target.nodeIds.length > 0) {
        return (
          <ContextMenu key={row.original.id}>
            <ContextMenuTrigger asChild>{TableComponents}</ContextMenuTrigger>
            <ContextMenuContent className="w-48">
              <MenuProvider primitives={contextPrimitives}>
                <ShareDownloadMenuItem
                  shareId={shareDownload.shareId}
                  downloadableVariants={shareDownload.downloadableVariants}
                  nodeIds={target.nodeIds}
                  assets={target.assets}
                  name={shareDownload.name ?? target.name}
                  includeOriginalFile={shareDownload.includeOriginalFile}
                />
              </MenuProvider>
            </ContextMenuContent>
          </ContextMenu>
        );
      }

      return TableComponents;
    }

    return (
      <ContextMenu key={row.original.id}>
        <ContextMenuTrigger disabled={someTrashedAssetsSelected && !onlyTrashedAssetsSelected} asChild>
          {TableComponents}
        </ContextMenuTrigger>
        <ContextMenuContent className="w-48">
          <MenuProvider primitives={contextPrimitives}>
            {trashPage ? (
              <RestoreTrashMenuItem
                label={`Restore item${selectedAssets.length === 0 ? '/s' : selectedAssets?.length > 1 ? 's' : ''}`}
                disabled={selectedAssets.length === 0}
              />
            ) : (
              <>
                {
                  // Hide download, share and "Open parent folder" options when uploading
                  !isUpload && (
                    <>
                      <DownloadMenuItem asset={row} disabled={!row.original.url || selectedAssets.length > 1} />
                      {!isDuplicatesPage && !isSharesPage && !search && selectedNode?.type === DAMNodeType.Folder && (
                        <DuplicateAssetMenuItem
                          asset={row}
                          selectedAssets={selectedAssets}
                          disabled={
                            selectedAssets.length > 1
                              ? selectedAssets.some((selection) => isAssetLoading(selection))
                              : isAssetLoading(row.original)
                          }
                          destinationFolderId={selectedNode.node.id}
                          isAllSelected={isAllSelected}
                          rowCount={rowCount ?? 0}
                          includeSubFolderAssets={includeSubFolderAssets}
                        />
                      )}
                      <DownloadZipMenuItem
                        nodeIds={target.nodeIds}
                        name={target.name}
                        sourceType={target.sourceType}
                        assets={target.assets}
                      />
                      <MenuSeparator />
                      {!isSharesPage && (selectedNode?.type !== DAMNodeType.Folder || search) && (
                        <>
                          <OpenParentFolderMenuItem asset={row} disabled={selectedAssets.length > 1} />
                          <MenuSeparator />
                        </>
                      )}
                      <ShareAssetMenuItem asset={row.original} disabled={selectedAssets.length > 1} />
                      <MenuSeparator />
                    </>
                  )
                }
                <DeleteNodeMenuItem
                  node={{
                    id: row.original.id,
                    node: {
                      id: row.original.id,
                      name: row.original.name,
                      slug: row.original.slug,
                    },
                    // We only use DeleteNodeMenuItem here to remove assets from
                    // albums so we can safely set the type to just `assets`
                    type: NodeType.Assets,
                  }}
                  isUpload={isUpload}
                  disabled={isUpload && uploadState !== TaskState.NotStarted}
                  assetCount={isUpload ? uploadSelectedAssetIds.length : rowCount}
                  label={tSelection('actionTooltip', {
                    action: tSelection(
                      isUpload || selectedNode?.type === DAMNodeType.Album ? 'removeAction' : 'trashAction',
                    ),
                    count: isUpload ? uploadSelectedAssetIds.length : selectedAssets.length,
                    type: 'asset',
                  })}
                  icon={isUpload || selectedNode?.type === DAMNodeType.Album ? ImageMinus : Trash2}
                  danger
                />
              </>
            )}
          </MenuProvider>
        </ContextMenuContent>
      </ContextMenu>
    );
  },
);

DraggableItemInner.displayName = 'DraggableItemInner';

function DraggableItemWrapper<TData extends Asset>(
  props: DraggableItemProps<TData>,
  ref?: Ref<HTMLTableRowElement | HTMLDivElement>,
) {
  return <DraggableItemInner {...(props as DraggableItemProps<any>)} ref={ref} />;
}

export const DraggableItem = forwardRef(DraggableItemWrapper) as <TData extends Asset>(
  props: DraggableItemProps<TData> & {
    ref?: Ref<HTMLTableRowElement | HTMLDivElement>;
  },
) => ReactElement | null;
